import type { ChangeEvent, KeyboardEvent, PointerEvent, Ref } from 'react';
import { Mic, Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import { Spinner, Tooltip } from '@/shared/ui';
import css from './agent-search-bar.module.scss';

const styles = pickCss(css, css.root);

const ENTER_KEY = 'Enter';
const WAVEFORM_BARS = 5;

export type AgentSearchBarProps = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  /** True while a planner / command-step / workflow mutation is in flight. */
  isSearching?: boolean;
  /** True between mic release and the transcribe response. */
  isTranscribing?: boolean;
  placeholder?: string;
  inputRef?: Ref<HTMLInputElement>;
  isMicSupported: boolean;
  isMicDenied?: boolean;
  isListening: boolean;
  audioLevel?: number;
  onMicHoldStart: () => void;
  onMicHoldEnd: () => void;
};

export const AgentSearchBar = ({
  value,
  onChange,
  onSubmit,
  isSearching = false,
  isTranscribing = false,
  placeholder = 'Ask the agent…',
  inputRef,
  isMicSupported,
  isMicDenied = false,
  isListening,
  audioLevel = 0,
  onMicHoldStart,
  onMicHoldEnd,
}: AgentSearchBarProps) => {
  const micRef = useRef<HTMLButtonElement>(null);
  const keyHeldRef = useRef(false);

  useEffect(() => {
    if (!isListening) return;
    window.addEventListener('pointerup', onMicHoldEnd);
    window.addEventListener('pointercancel', onMicHoldEnd);
    return () => {
      window.removeEventListener('pointerup', onMicHoldEnd);
      window.removeEventListener('pointercancel', onMicHoldEnd);
    };
  }, [isListening, onMicHoldEnd]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ENTER_KEY) {
      event.preventDefault();
      onSubmit();
    }
  };

  const handleClear = () => {
    if (isListening) {
      onMicHoldEnd();
    }
    onChange('');
    if (inputRef && typeof inputRef === 'object' && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleMicPointerDown = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return;
      micRef.current?.setPointerCapture?.(event.pointerId);
      onMicHoldStart();
    },
    [onMicHoldStart]
  );

  const handleMicPointerUp = useCallback(() => {
    onMicHoldEnd();
  }, [onMicHoldEnd]);

  const handleMicPointerCancel = useCallback(() => {
    onMicHoldEnd();
  }, [onMicHoldEnd]);

  const handleMicKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== ' ' && event.key !== ENTER_KEY) return;
      if (event.repeat) return;
      event.preventDefault();
      keyHeldRef.current = true;
      onMicHoldStart();
    },
    [onMicHoldStart]
  );

  const handleMicKeyUp = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== ' ' && event.key !== ENTER_KEY) return;
      if (!keyHeldRef.current) return;
      keyHeldRef.current = false;
      onMicHoldEnd();
    },
    [onMicHoldEnd]
  );

  const showTrailing = value.length > 0 && !isListening && !isTranscribing;
  const showLeadingSpinner = isSearching || isTranscribing;

  return (
    <div
      className={styles({
        is: isListening ? 'listening' : undefined,
      })}
    >
      {isListening ? (
        <span aria-hidden="true" className={css.listeningWaveform}>
          {Array.from({ length: WAVEFORM_BARS }, (_, i) => (
            <span
              className={css.listeningWaveformBar}
              key={i}
              style={{
                animationDelay: `${i * 90}ms`,
                transformOrigin: 'center',
                transform: `scaleY(${audioLevel * (1 - i * 0.15)})`,
              }}
            />
          ))}
        </span>
      ) : showLeadingSpinner ? (
        <span
          aria-label={isTranscribing ? 'Transcribing' : 'Searching'}
          className={css.leadingIcon}
          role="status"
        >
          <Spinner size="sm" />
        </span>
      ) : (
        <span aria-hidden="true" className={css.leadingIcon}>
          <Search size={16} />
        </span>
      )}
      <input
        aria-label="Search the agent"
        className={css.field}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          onChange(event.target.value);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        ref={inputRef}
        type="text"
        value={value}
      />
      {showTrailing && (
        <>
          <button
            aria-label="Clear search"
            className={css.clearBtn}
            data-testid="clear-btn"
            onClick={handleClear}
            type="button"
          >
            <X aria-hidden="true" size={12} />
          </button>
          <span aria-hidden="true" className={css.shortcutChip}>
            ⏎
          </span>
        </>
      )}
      {!isMicSupported && isMicDenied && (
        <Tooltip
          content="Allow microphone access in your browser settings to record."
          position="bottom"
        >
          <button
            aria-label="Microphone access denied"
            className={joinCss(css.micBtn, css.micBtnIsDisabled)}
            data-testid="mic-denied-btn"
            disabled
            type="button"
          >
            <Mic aria-hidden="true" size={18} />
          </button>
        </Tooltip>
      )}
      {isMicSupported && (
        <button
          aria-label={
            isTranscribing
              ? 'Transcribing'
              : isListening
                ? 'Recording — release to fill'
                : 'Hold to record'
          }
          className={joinCss(css.micBtn, isListening && css.micBtnIsActive)}
          data-testid="mic-btn"
          disabled={isTranscribing}
          onKeyDown={handleMicKeyDown}
          onKeyUp={handleMicKeyUp}
          onPointerCancel={handleMicPointerCancel}
          onPointerDown={handleMicPointerDown}
          onPointerUp={handleMicPointerUp}
          ref={micRef}
          type="button"
        >
          {isTranscribing ? (
            <Spinner size="sm" />
          ) : (
            <Mic aria-hidden="true" size={18} />
          )}
        </button>
      )}
    </div>
  );
};
